/**
 * Spec 037: «О компании для сотрудников» — what we do, for whom, our directions and what we do
 * not do. Every agent of the company gets it at its start and again when it changes. Partners
 * change it here, or their own agent with their words; each change is a version with its author.
 * A tab of «Отделы» (`?tab=brief`), not a page of its own: the header and the tabs stay.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { CompanyBrief } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { ago, Caption, EmptyNote, whoDid } from "@/kit";
import { Markdown } from "@/lib/markdown";

export function CompanyBriefBlock() {
  const qc = useQueryClient();
  const toast = useToast();
  const brief = useQuery({ queryKey: ["company", "brief"], queryFn: CompanyBrief.get });
  const versions = useQuery({ queryKey: ["company", "versions"], queryFn: CompanyBrief.versions });
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => CompanyBrief.set((draft ?? "").trim(), brief.data?.version ?? null),
    onSuccess: (saved) => {
      setDraft(null);
      toast.success(`Сохранено — версия ${saved.version}. Агенты получат её со следующим шагом.`);
      void qc.invalidateQueries({ queryKey: ["company"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["company"] });
    },
  });

  if (brief.isLoading) return <SkeletonList count={3} />;
  const current = brief.data ?? null;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-meta text-p-text-muted">
        Каждый агент отделов получает этот текст при запуске и снова, когда он меняется: чем мы занимаемся, для кого, направления и чего не делаем. До 4000 знаков.
      </p>
      {draft === null ? (
        <>
          {current ? (
            <section className="rounded-card border border-p-border bg-p-surface px-4 py-3.5">
              <Markdown className="text-body text-p-text" text={current.text_md} />
              <p className="mt-2 text-meta text-p-text-muted">
                Версия {current.version} · {whoDid(current.author.name, current.via_agent)} · {ago(current.created_at)}
              </p>
            </section>
          ) : (
            <EmptyNote>Документа ещё нет: агенты работают, не зная, чем занимается компания.</EmptyNote>
          )}
          <div>
            <Button variant="secondary" onClick={() => setDraft(current?.text_md ?? "")}>{current ? "Изменить" : "Написать"}</Button>
          </div>
        </>
      ) : (
        <section className="flex flex-col gap-2">
          <TextArea label="Текст" value={draft} rows={14} maxLength={4000} onChange={(e) => setDraft(e.target.value)} />
          <div className="flex gap-2">
            <Button variant="strong" disabled={!draft.trim()} loading={save.isPending} onClick={() => save.mutate()}>Сохранить</Button>
            <Button variant="secondary" onClick={() => setDraft(null)}>Отмена</Button>
          </div>
        </section>
      )}
      {(versions.data ?? []).length > 1 && (
        <section className="space-y-1.5">
          <Caption>История</Caption>
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {(versions.data ?? []).map((v) => (
              <li key={v.version} className="space-y-0.5 border-b border-p-border px-4 py-3 last:border-0">
                <p className="text-body font-semibold text-p-text">Версия {v.version}</p>
                <p className="text-meta text-p-text-muted">{whoDid(v.author.name, v.via_agent)} · {ago(v.created_at)}</p>
                {v.user_confirmation && <p className="break-words text-meta text-p-text">«{v.user_confirmation}»</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
